// client/src/components/ticketing/TicketPurchasePanel.jsx
//
// Lets a guest buy tickets via the tenant's configured payment provider
// (Stripe or Square) — no login required. Direct structural mirror of
// RafflePurchasePanel.jsx: the server's /purchase-intent response tells us
// which checkout UI to render via the same provider-agnostic
// <UnifiedCheckout/>. Each purchased ticket becomes one row in t_ticket
// (server-side, in ticketingRoutes.finalizeTicketPurchase). The payment
// webhook is the safety net if the buyer closes the tab mid-flow.

import React, { useState, useMemo } from 'react';
import axios from 'axios';
import UnifiedCheckout from '../payment/UnifiedCheckout';
import MyTicket from './MyTicket';
import { formatCurrency } from '../../utils/currencyUtils';

export default function TicketPurchasePanel({
  tenantId, eventId, itemId,
  tier,
  // Buyer details + custom-field answers — shared state with the parent form.
  buyer,          // { name, email, phone, attributes }
  buyerValid,
  promoCode,
  buyerCountry,
  buyerPostalCode,
}) {
  const [numTickets, setNumTickets] = useState(Number(tier.min_per_order) || 1);
  const [intent, setIntent] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null); // { tickets, pendingWebhook }

  const minPerOrder = Math.max(1, Number(tier.min_per_order) || 1);
  const maxPerOrder = Math.max(minPerOrder, Number(tier.max_per_order) || 10);
  const total = useMemo(
    () => Math.round((Number(numTickets) || 0) * (Number(tier.price) || 0) * 100) / 100,
    [numTickets, tier.price]
  );
  const totalLabel = formatCurrency(total, tier.currency);

  const startPurchase = async () => {
    setError(null);
    if (!buyerValid) {
      setError('Please complete the required fields above first.');
      return;
    }
    const n = Math.max(minPerOrder, Math.min(maxPerOrder, Number(numTickets) || minPerOrder));
    setBusy(true);
    try {
      const { data } = await axios.post(
        `/api/ticketing/public/${tenantId}/event/${eventId}/item/${itemId}/purchase-intent`,
        {
          name: buyer.name,
          email: buyer.email,
          phone: buyer.phone || null,
          attributes: buyer.attributes || {},
          num_tickets: n,
          tier_id: tier.id,
          promo_code: promoCode || null,
          buyer_country: buyerCountry || null,
          buyer_postal_code: buyerPostalCode || null,
          utm_source: new URLSearchParams(window.location.search).get('utm_source') || null,
          utm_medium: new URLSearchParams(window.location.search).get('utm_medium') || null,
          utm_campaign: new URLSearchParams(window.location.search).get('utm_campaign') || null,
          referrer: document.referrer || null,
          // Multi-touch attribution: the os_vid first-party cookie set when the
          // visitor first clicked a tracked campaign link (On-Screen 2.0).
          os_vid: (document.cookie.match(/(?:^|;\s*)os_vid=([a-f0-9]{32})/) || [])[1] || null,
        }
      );
      if (data?.success) {
        setIntent({
          provider: data.provider || 'stripe',
          clientSecret: data.clientSecret || null,
          sdkConfig: data.sdkConfig || null,
          paymentIntentId: data.paymentIntentId || null,
          orderId: data.orderId,
          subtotal: data.subtotal,
          discount: data.discount,
          tax: data.tax,
          total: data.total,
          currency: data.currency || tier.currency,
        });
      } else {
        setError(data?.message || 'Could not start checkout');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Could not start checkout');
    } finally {
      setBusy(false);
    }
  };

  // Called by UnifiedCheckout after the provider's client SDK does its part.
  const handleCheckoutSuccess = async (payload) => {
    try {
      const { data } = await axios.post(
        `/api/ticketing/public/${tenantId}/event/${eventId}/item/${itemId}/purchase-confirm`,
        {
          orderId: intent.orderId,
          providerPaymentId: payload.providerPaymentId || null,
          sourceToken: payload.sourceToken || null,
        }
      );
      if (data?.success) {
        setSuccess({ tickets: data.tickets || [], pendingWebhook: false });
      } else {
        setSuccess({ tickets: [], pendingWebhook: true });
      }
    } catch (postErr) {
      console.warn('purchase-confirm post failed; webhook fallback:', postErr?.message);
      setSuccess({ tickets: [], pendingWebhook: true });
    }
  };

  const reset = () => {
    setIntent(null);
    setSuccess(null);
    setError(null);
    setNumTickets(minPerOrder);
  };

  if (success) {
    return (
      <MyTicket
        tenantId={tenantId}
        tickets={success.tickets}
        pendingWebhook={success.pendingWebhook}
        onBuyMore={reset}
      />
    );
  }

  return (
    <div className="mt-4 rounded-xl border border-black/10 dark:border-white/10 p-4">
      <h4 className="font-semibold mb-2">Buy {tier.name} tickets</h4>
      <p className="text-sm opacity-70 mb-3">{formatCurrency(tier.price, tier.currency)} per ticket</p>

      {!intent && (
        <>
          <label className="block text-sm mb-1" htmlFor="num_tickets">
            Number of tickets
          </label>
          <input
            id="num_tickets"
            type="number"
            min={minPerOrder}
            max={maxPerOrder}
            step="1"
            value={numTickets}
            onChange={(e) => {
              const v = Math.max(minPerOrder, Math.min(maxPerOrder, Number(e.target.value) || minPerOrder));
              setNumTickets(v);
            }}
            disabled={busy}
            className="w-full px-3 py-2 rounded border bg-transparent mb-1"
          />
          <p className="text-xs opacity-60 mb-3">
            {minPerOrder === maxPerOrder ? `Exactly ${minPerOrder} per order.` : `Between ${minPerOrder} and ${maxPerOrder} per order.`}
          </p>

          <div className="text-lg mb-3">
            Total: <strong className="text-brand-orange">{totalLabel}</strong>
          </div>

          {error && <div className="text-red-600 dark:text-red-400 text-sm mb-3">{error}</div>}

          <button
            type="button"
            onClick={startPurchase}
            disabled={busy || !buyerValid}
            className="w-full px-4 py-2 rounded bg-cobalt text-white font-semibold disabled:opacity-50"
          >
            {busy ? 'Starting…' : `Checkout — ${totalLabel}`}
          </button>
          {!buyerValid && (
            <p className="mt-2 text-xs opacity-60">Complete the required fields above to enable checkout.</p>
          )}
        </>
      )}

      {intent && (
        <>
          <div className="rounded-lg border border-black/10 dark:border-white/10 p-3 mb-3 text-sm space-y-1">
            <div className="flex justify-between"><span className="opacity-70">Subtotal</span><span>{formatCurrency(intent.subtotal, intent.currency)}</span></div>
            {intent.discount > 0 && (
              <div className="flex justify-between"><span className="opacity-70">Discount</span><span>-{formatCurrency(intent.discount, intent.currency)}</span></div>
            )}
            {intent.tax > 0 && (
              <div className="flex justify-between"><span className="opacity-70">Tax</span><span>{formatCurrency(intent.tax, intent.currency)}</span></div>
            )}
            <div className="flex justify-between font-semibold pt-1 border-t border-black/10 dark:border-white/10">
              <span>Total</span><span>{formatCurrency(intent.total, intent.currency)}</span>
            </div>
          </div>
          <UnifiedCheckout
            provider={intent.provider}
            clientSecret={intent.clientSecret}
            sdkConfig={intent.sdkConfig}
            amountLabel={formatCurrency(intent.total, intent.currency)}
            onSuccess={handleCheckoutSuccess}
            onError={(msg) => setError(msg)}
            onCancel={reset}
          />
          {error && <div className="text-red-600 dark:text-red-400 text-sm mt-3">{error}</div>}
        </>
      )}
    </div>
  );
}
